import {
  AbilityBuilder,
  AbilityTuple,
  MongoAbility,
  MongoQuery,
  createMongoAbility,
} from '@casl/ability';
import { Role, User, AuthUser } from './user.interface';

export enum Action {
  Manage = 'manage',
  Create = 'create',
  Read = 'read',
  Update = 'update',
  Delete = 'delete',
}

// Define subjects that can be checked for permissions
// We can use string names or actual classes/interfaces
export type Subjects =
  | AbilityTuple
  | string
  | 'Playlist'
  | 'User'
  | 'Comment'
  | 'Video'
  | 'all';

export type AppAbility = MongoAbility<[Action, Subjects], MongoQuery>;

export function defineAbilityRules(
  { can }: AbilityBuilder<AppAbility>,
  user?: User | AuthUser | { role: Role; id: string } | null,
): void {
  if (user?.role === Role.Admin) {
    can(Action.Manage, 'all');
  } else {
    can(Action.Read, 'all');
  }

  if (user?.role === Role.User) {
    const userId =
      'id' in user ? user.id : 'userId' in user ? user.userId : undefined;
    can(Action.Manage, 'Playlist', { ownerId: userId });
    can(Action.Create, 'Playlist');
  }

  if (user?.role === Role.Moderator) {
    can(Action.Delete, 'Playlist');
    can(Action.Update, 'Playlist');
  }
}

export function createAbility(
  user?: User | AuthUser | { role: Role; id: string } | null,
): AppAbility {
  const builder = new AbilityBuilder<AppAbility>(createMongoAbility);
  defineAbilityRules(builder, user);
  return builder.build({
    detectSubjectType: (item) => {
      // For plain objects, infer type from properties
      if (item && typeof item === 'object') {
        // Playlist has shortId, slug, or ownerId
        if ('ownerId' in item || 'shortId' in item || 'slug' in item) {
          return 'Playlist';
        }
        // User has userId or role
        if ('userId' in item || ('role' in item && 'username' in item)) {
          return 'User';
        }
      }
      // Fallback to string subjects
      return item as unknown as string;
    },
  });
}
